/**
 * Application Principale - Générateur 3D de Tonnelles
 * 
 * Architecture modulaire selon cahier des charges
 * Isolation totale des modules - Performance optimale
 */

import React, { useState, useCallback, useEffect } from 'react';
import './App.css';
import { StructureBaseModule, StructureBase, TypeForme, TypeMateriauGlobal } from './modules/structure-base';
import { TraversesModule, StructureTraverses } from './modules/traverses';
import { SouduresModule, StructureSoudures, DonneesTonnelle } from './modules/soudures';
import { DoublePoutreModule, StructureDoublePoutre } from './modules/double-poutre';
import { StructureTraversesDoublePoutre } from './modules/double-poutre/traverses/types';
import { callStructureBaseAPI } from './modules/structure-base';
import { ToitModule, StructureToit } from './modules/toit';
import { AccessoiresModule, StructureAccessoires } from './modules/accessoires';
import { ParoisModule, StructureParois, DonneesStructureBasePourParois } from './modules/parois';
import { PortesModule, StructurePortes, DonneesStructureBasePourPortes } from './modules/portes';
import Visualisateur3D from './components/Visualisateur3D';
import AdminPanel from './components/AdminPanel/AdminPanel';
import { useModuleManager } from './services/ModuleManager';
import { GeometryUtils } from './modules/structure-base/services/StructureBase3DService';
import ModelApiService from './services/ModelApiService';

// Composant de gestion d'authentification
function AuthManager({ children }: { children: React.ReactNode }) {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Vérifier si un token est passé dans l'URL
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    
    if (token) {
      // Stocker le token et marquer comme authentifié
      localStorage.setItem('matonnelle_token', token);
      localStorage.setItem('matonnelle_auth', 'true');
      console.log('🔑 Token reçu et stocké:', token.substring(0, 20) + '...');
      
      // Nettoyer l'URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // BYPASS D'AUTHENTIFICATION (DEV ou flag explicite)
    const bypassParam = urlParams.get('bypass') === '1' || urlParams.get('bypassAuth') === '1';
    if (bypassParam) {
      localStorage.setItem('dev_bypass_auth', 'true');
      console.log('🟢 Bypass auth activé via paramètre d\'URL.');
      // Nettoyer l'URL pour retirer le paramètre
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    const bypassAuth = (
      bypassParam ||
      localStorage.getItem('dev_bypass_auth') === 'true' ||
      process.env.NODE_ENV !== 'production' ||
      process.env.REACT_APP_BYPASS_AUTH === 'true'
    );

    if (bypassAuth) {
      if (localStorage.getItem('matonnelle_auth') !== 'true') {
        localStorage.setItem('matonnelle_auth', 'true');
      }
      console.log('🟢 Bypass auth activé (dev/flag). Accès direct à l\'application.');
      setIsReady(true);
      return;
    }
    
    // Vérifier l'authentification existante
    const auth = localStorage.getItem('matonnelle_auth');
    if (auth !== 'true') {
      // Rediriger vers le site de connexion
      console.log('❌ Non authentifié, redirection vers le site de connexion');
      window.location.href = '/login';
      return;
    }
    
    console.log('✅ Authentifié, accès autorisé à l\'application');
    setIsReady(true);
  }, []);

  if (!isReady) {
    return (
      <div style={{ 
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#1a1a1a',
        color: 'white' 
      }}>
        <div>Initialisation de l'application...</div>
      </div>
    );
  }

  return <>{children}</>;
}

// Styles de l'application - Thème sombre
const styles = {
  app: {
    display: 'flex',
    minHeight: '100vh',
    fontFamily: 'Arial, sans-serif',
    backgroundColor: '#1a1a1a'
  } as React.CSSProperties,
  sidebar: {
    width: '350px',
    minWidth: '350px',
    backgroundColor: '#2d2d2d',
    borderRight: '2px solid #404040',
    overflowY: 'auto' as const,
    padding: '0',
    boxShadow: '2px 0 5px rgba(0,0,0,0.3)',
    maxHeight: '100vh'
  } as React.CSSProperties,
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    padding: '20px',
    backgroundColor: '#1a1a1a'
  } as React.CSSProperties,
  header: {
    backgroundColor: '#0d6efd',
    color: 'white',
    padding: '15px 20px',
    margin: '0',
    fontSize: '20px',
    fontWeight: 'bold',
    borderBottom: '3px solid #0a58ca'
  } as React.CSSProperties,
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '20px'
  } as React.CSSProperties,
  status: {
    padding: '10px 20px',
    backgroundColor: '#1e3a5f',
    border: '1px solid #2c5aa0',
    borderRadius: '4px',
    fontSize: '14px',
    color: '#87ceeb'
  } as React.CSSProperties,
  error: {
    padding: '10px 20px',
    backgroundColor: '#4a1e1e',
    border: '1px solid #8b3a3a',
    borderRadius: '4px',
    fontSize: '14px',
    color: '#ff6b6b'
  } as React.CSSProperties,
  modulesList: {
    padding: '20px',
    borderTop: '1px solid #404040',
    backgroundColor: '#333333'
  } as React.CSSProperties,
  moduleItem: {
    padding: '10px 12px',
    borderBottom: '1px solid #404040',
    color: '#ddd',
    fontSize: '14px'
  } as React.CSSProperties,
  moduleActive: {
    color: '#9BE169',
    fontWeight: 'bold'
  } as React.CSSProperties,
  adminButton: {
    position: 'absolute' as const,
    top: '10px',
    right: '10px',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 12px',
    fontSize: '12px',
    cursor: 'pointer',
    zIndex: 1000
  } as React.CSSProperties,
  actionsBar: {
    display: 'flex',
    gap: '8px',
    padding: '10px 12px',
    borderBottom: '1px solid #404040',
    backgroundColor: '#2d2d2d'
  } as React.CSSProperties,
  resetButton: {
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '6px 10px',
    fontSize: '12px',
    cursor: 'pointer'
  } as React.CSSProperties,
  regenButton: {
    backgroundColor: '#0d6efd',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '6px 10px',
    fontSize: '12px',
    cursor: 'pointer'
  } as React.CSSProperties
};

function App() {
  // Calcule le centre géométrique réel selon le type de forme
  const calculerCentreGeometrique = (structure: StructureBase): { x: number; y: number; z: number } => {
    const typeForm = structure.parametres.type.toLowerCase();
    
    // Pour toutes les formes géométriques standard, le centre est à l'origine
    // car elles sont générées centrées dans le serveur
    switch (typeForm) {
      case 'cercle':
      case 'ovale':
      case 'carre':
      case 'rectangle':
      case 'polygone':
        return { x: 0, y: 0, z: 0 };
      
      default:
        // Pour les formes personnalisées ou non standard, utiliser la moyenne des points
        return GeometryUtils.calculerCentre(structure.poutres?.poutreHaute?.chemin || []);
    }
  };
  // État de l'application
  const [structure, setStructure] = useState<StructureBase | undefined>();
  const [traverses, setTraverses] = useState<StructureTraverses | null>(null);
  const [soudures, setSoudures] = useState<StructureSoudures | null>(null);
  const [doublePoutre, setDoublePoutre] = useState<StructureDoublePoutre | null>(null);
  const [doublePoutreActive, setDoublePoutreActive] = useState(false);
  const [traversesDoublePoutre, setTraversesDoublePoutre] = useState<StructureTraversesDoublePoutre | null>(null);
  const [toit, setToit] = useState<StructureToit | null>(null);
  const [accessoires, setAccessoires] = useState<StructureAccessoires | null>(null);
  const [parois, setParois] = useState<StructureParois | null>(null);
  const [portes, setPortes] = useState<StructurePortes | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  
  // Gestionnaire de modules
  const { moduleStates, isModuleActive, registerModule, toggleModule, activateModule, resetAllModules } = useModuleManager();

  // Debug: Log des états des modules
  useEffect(() => {
    console.log('🔍 DEBUG App.tsx - États des modules:', moduleStates);
    console.log('🔍 DEBUG App.tsx - Module toit actif?', isModuleActive('toit'));
    console.log('🔍 DEBUG App.tsx - Module accessoires actif?', isModuleActive('accessoires'));
  }, [moduleStates, isModuleActive]);

  // Pas de synchronisation automatique - la checkbox interne du module gère son propre état
  // useEffect(() => {
  //   setDoublePoutreActive(isModuleActive('double-poutre'));
  // }, [moduleStates, isModuleActive]);

  // Initialisation des modules
  useEffect(() => {
    // Enregistrer les modules existants
    registerModule({
      id: 'structure-base',
      name: 'Structure de Base',
      active: true,
      dependencies: [],
      required: true
    });
    
    registerModule({
      id: 'traverses',
      name: 'Les Traverses',
      active: true,
      dependencies: ['structure-base']
    });
    
    registerModule({
      id: 'soudures',
      name: 'Les Soudures',
      active: true,
      dependencies: ['structure-base', 'traverses']
    });
    
    registerModule({
      id: 'double-poutre',
      name: 'Double Poutre',
      active: false, // Désactivé par défaut
      dependencies: ['structure-base']
    });
    
    registerModule({
      id: 'toit',
      name: 'Le Toit',
      active: true,
      dependencies: ['structure-base']
    });

    // Module 6: Accessoires
    registerModule({
      id: 'accessoires',
      name: 'Accessoires et Environnement',
      active: true,
      dependencies: ['structure-base']
    });

    // Module 7: Parois
    registerModule({
      id: 'parois',
      name: 'Les Parois',
      active: true,
      dependencies: ['structure-base']
    });

    // Module 8: Portes
    registerModule({
      id: 'portes',
      name: 'Les Portes',
      active: true,
      dependencies: ['structure-base']
    });
  }, []); // Exécuter seulement au montage du composant

  // Module accessoires géré manuellement par l'utilisateur

  // Création d'une structure par défaut au démarrage
  useEffect(() => {
    const creerStructureParDefaut = async () => {
      try {
        console.log('🏗️ Création de la structure par défaut...');
        console.log('📊 État actuel de structure:', structure);
        const parametresDefaut = {
          type: TypeForme.RECTANGLE,
          largeur: 4,
          longueur: 6,
          hauteur: 2.5,
          materiau: TypeMateriauGlobal.FER_A_BETON,
          epaisseur: 20,
          couleur: '#8B4513'
        };
        
        const structureDefaut = await callStructureBaseAPI(parametresDefaut, 4);
        console.log('✅ Structure par défaut créée:', structureDefaut);
        console.log('🔍 Type de structure retournée:', typeof structureDefaut, structureDefaut ? 'non-null' : 'null');
        setStructure(structureDefaut);
        console.log('📝 Structure définie dans l\'état');
    
      } catch (err) {
        const errorMsg = `Erreur création structure par défaut: ${err instanceof Error ? err.message : 'Erreur inconnue'}`;
        console.error('❌', errorMsg, err);
        setError(errorMsg);
      }
    };
    
    creerStructureParDefaut();
  }, []);

  /**
   * Gestion des changements de structure (isolation d'erreur)
   */
  const handleStructureChange = useCallback((newStructure: StructureBase) => {
    try {
      setStructure(newStructure);
      setError(null);
  
    } catch (err) {
      const errorMsg = `Erreur mise à jour structure: ${err instanceof Error ? err.message : 'Erreur inconnue'}`;
      console.error('❌ Erreur handleStructureChange:', err);
      setError(errorMsg);
    }
  }, []);

  /**
   * Gestion des changements de traverses
   */
  const handleTraversesChange = useCallback((newTraverses: StructureTraverses | null) => {
    try {
      setTraverses(newTraverses);
  
    } catch (err) {
      const errorMsg = `Erreur mise à jour traverses: ${err instanceof Error ? err.message : 'Erreur inconnue'}`;
      setError(errorMsg);
    }
  }, []);

  /**
   * Gestion des changements de soudures
   */
  const handleSouduresChange = useCallback((newSoudures: StructureSoudures | null) => {
    try {
      setSoudures(newSoudures);
  
    } catch (err) {
      const errorMsg = `Erreur mise à jour soudures: ${err instanceof Error ? err.message : 'Erreur inconnue'}`;
      setError(errorMsg);
    }
  }, []);

  /**
   * Gestion des changements de la double poutre
   */
  const handleDoublePoutreChange = useCallback((newDoublePoutre: StructureDoublePoutre | null) => {
    try {
      setDoublePoutre(newDoublePoutre);
  
    } catch (err) {
      const errorMsg = `Erreur mise à jour double poutre: ${err instanceof Error ? err.message : 'Erreur inconnue'}`;
      setError(errorMsg);
    }
  }, []);

  /**
   * Gestion de l'activation/désactivation de la double poutre (checkbox interne)
   */
  const handleDoublePoutreToggle = useCallback(async (actif: boolean) => {
    try {
      setDoublePoutreActive(actif);
      
      // Ne pas synchroniser avec le gestionnaire de modules ici
      // Cette fonction gère uniquement la checkbox interne du module
      
      if (!actif) {
        setDoublePoutre(null);
        setTraversesDoublePoutre(null);
      }
  
    } catch (err) {
      const errorMsg = `Erreur toggle double poutre: ${err instanceof Error ? err.message : 'Erreur inconnue'}`;
      setError(errorMsg);
    }
  }, []);

  /**
   * Gestion des changements des traverses de la double poutre
   */
  const handleTraversesDoublePoutreChange = useCallback((traverses: StructureTraversesDoublePoutre | null) => {
    try {
      setTraversesDoublePoutre(traverses);
    } catch (err) {
      const errorMsg = `Erreur mise à jour traverses double poutre: ${err instanceof Error ? err.message : 'Erreur inconnue'}`;
      setError(errorMsg);
    }
  }, []);

  /**
   * Gestion des changements du toit
   */
  const handleToitChange = useCallback((newToit: StructureToit | null) => {
    try {
      setToit(newToit);
    } catch (err) {
      const errorMsg = `Erreur mise à jour toit: ${err instanceof Error ? err.message : 'Erreur inconnue'}`;
      setError(errorMsg);
    }
  }, []);

  /**
   * Gestion des changements des paramètres du toit
   */
  const handleToitParametresChange = useCallback((parametres: any) => {
    try {
      console.log('🏠 Paramètres toit changés:', parametres);
      // Mise à jour synchrone pour éviter le flash rectangulaire
      setToit(prev => {
        // Retourner une nouvelle référence pour déclencher le re-render
        return prev ? { ...prev, parametres } : prev;
      });
    } catch (err) {
      const errorMsg = `Erreur mise à jour paramètres toit: ${err instanceof Error ? err.message : 'Erreur inconnue'}`;
      setError(errorMsg);
    }
  }, []);

  const handleParoisChange = useCallback((newParois: StructureParois | null) => {
    try {
      setParois(newParois);
    } catch (err) {
      const errorMsg = `Erreur mise à jour parois: ${err instanceof Error ? err.message : 'Erreur inconnue'}`;
      setError(errorMsg);
    }
  }, []);

  const handlePortesChange = useCallback((newPortes: StructurePortes | null) => {
    try {
      setPortes(newPortes);
    } catch (err) {
      const errorMsg = `Erreur mise à jour portes: ${err instanceof Error ? err.message : 'Erreur inconnue'}`;
      setError(errorMsg);
    }
  }, []);

  /**
   * Gestion des erreurs des modules (isolation)
   */
  const handleModuleError = useCallback((moduleError: string) => {
    setError(moduleError);
  }, []);

  // Ajout: Réinitialisation complète (localStorage, modules, caches, états) puis régénération par défaut
  const handleResetAll = useCallback(async () => {
    try {
      setLoading(true);

      // Nettoyage des stockages locaux (clés connues)
      try {
        localStorage.removeItem('module-states');
        localStorage.removeItem('module-comments');
        localStorage.removeItem('matonnelle_auth');
        localStorage.removeItem('matonnelle_token');
        localStorage.removeItem('dev_bypass_auth');
      } catch (e) {
        console.warn('Avertissement lors du nettoyage du localStorage:', e);
      }

      // Réinitialiser les modules (ModuleManager)
      try {
        resetAllModules();
      } catch (e) {
        console.warn('Avertissement lors de resetAllModules:', e);
      }

      // Purger le cache des modèles 3D (si utilisé)
      try {
        ModelApiService.getInstance().clearCache();
      } catch (e) {
        console.warn('Avertissement lors de clearCache ModelApiService:', e);
      }

      // Réinitialiser tous les états React
      setStructure(undefined);
      setTraverses(null);
      setSoudures(null);
      setDoublePoutre(null);
      setTraversesDoublePoutre(null);
      setToit(null);
      setAccessoires(null);
      setError(null);

      // Régénérer une structure par défaut pour repartir propre
      const paramsDefaut = {
        type: TypeForme.RECTANGLE,
        largeur: 4,
        longueur: 6,
        hauteur: 2.5,
        materiau: TypeMateriauGlobal.FER_A_BETON,
        epaisseur: 20,
        couleur: '#8B4513'
      };
      const structureDefaut = await callStructureBaseAPI(paramsDefaut, 4);
      setStructure(structureDefaut);

    } catch (err) {
      const msg = `Erreur réinitialisation: ${err instanceof Error ? err.message : 'Erreur inconnue'}`;
      console.error(msg, err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [resetAllModules]);

  // Ajout: Forcer une régénération de la structure avec les paramètres courants (ou par défaut)
  const handleForceRegen = useCallback(async () => {
    try {
      setLoading(true);
      const params = structure?.parametres || {
        type: TypeForme.RECTANGLE,
        largeur: 4,
        longueur: 6,
        hauteur: 2.5,
        materiau: TypeMateriauGlobal.FER_A_BETON,
        epaisseur: 20,
        couleur: '#8B4513'
      };
      const nbMontants = structure?.montants?.length && structure.montants.length > 0 ? structure.montants.length : 4;
      const nouvelleStructure = await callStructureBaseAPI(params as any, nbMontants);
      setStructure(nouvelleStructure);
      setError(null);
    } catch (err) {
      const msg = `Erreur régénération: ${err instanceof Error ? err.message : 'Erreur inconnue'}`;
      console.error(msg, err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [structure]);

  return (
    <AuthManager>
      <div style={styles.app}>
        {/* Barre latérale et contenu, inchangé */}
        {/* Bouton d'administration */}
        <button 
          style={styles.adminButton}
          onClick={() => setShowAdminPanel(!showAdminPanel)}
          title={showAdminPanel ? "Retour à l'application" : "Ouvrir l'administration"}
        >
          {showAdminPanel ? '🏗️ App' : '⚙️ Admin'}
        </button>

        {showAdminPanel ? (
          /* Page d'administration */
          <AdminPanel />
        ) : (
          /* Interface normale */
          <>
            {/* Panneau latéral des modules */}
            <div style={styles.sidebar} className="app-sidebar">
              <div style={styles.header}>
                🏗️ Générateur 3D Tonnelles
              </div>
              <div style={styles.actionsBar}>
                <button style={styles.resetButton} onClick={handleResetAll} title="Effacer les états locaux et repartir propre">
                  Tout réinitialiser
                </button>
                <button style={styles.regenButton} onClick={handleForceRegen} title="Relancer les appels API de génération">
                  Forcer régénération
                </button>
              </div>

              {/* Affichage de l'état de chargement */}
              {loading && (
                <div style={{
                  ...styles.status,
                  backgroundColor: '#0066cc',
                  borderColor: '#004499',
                  color: 'white',
                  fontWeight: 'bold',
                  textAlign: 'center'
                }}>
                  ⏳ En cours d'exécution...
                </div>
              )}

              {/* Affichage des erreurs */}
              {error && (
                <div style={{
                  ...styles.error,
                  marginBottom: '10px',
                  cursor: 'pointer'
                }} onClick={() => setError(null)} title="Cliquer pour fermer">
                  ❌ {error}
                  <div style={{ fontSize: '10px', marginTop: '5px', opacity: 0.8 }}>
                    (Cliquer pour fermer)
                  </div>
                </div>
              )}

              {/* Module 1: Structure de Base */}
              <div style={{ 
                opacity: isModuleActive('structure-base') ? 1 : 0.5, 
                position: 'relative',
                pointerEvents: isModuleActive('structure-base') ? 'auto' : 'none'
              }}>
                <StructureBaseModule
                  onStructureChange={handleStructureChange}
                  onError={handleModuleError}
                />
                {!isModuleActive('structure-base') && (
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'rgba(255, 0, 0, 0.8)',
                    color: 'white',
                    padding: '10px 20px',
                    borderRadius: '5px',
                    fontWeight: 'bold',
                    zIndex: 1000
                  }}>              STRUCTURE DE BASE DÉSACTIVÉE            </div>
                )}
              </div>
              
              {/* Module 2: Les Traverses */}
              {structure && (
                isModuleActive('traverses') ? (
                  <TraversesModule
                    donneesStructureBase={{
                      forme: structure.parametres.type as string,
                      largeur: structure.parametres.largeur,
                      longueur: structure.parametres.type === 'cercle' || structure.parametres.type === 'carre'
                        ? structure.parametres.largeur
                        : (structure.parametres.longueur || structure.parametres.largeur),
                      hauteur: structure.parametres.hauteur,
                      nombreMontants: structure.montants.length,
                      positionsMontants: structure.montants.map(m => ({ x: m.position.x, y: m.position.y, z: m.position.z })),
                      materiau: structure.parametres.materiau,
                      epaisseur: structure.parametres.epaisseur,
                      couleur: structure.parametres.couleur, // Couleur globale pour les traverses
                      poutreHaute: {
                        points: structure.poutres?.poutreHaute?.chemin || [],
                        rayon: 0.05
                      },
                      poutreBasse: {
                        points: structure.poutres?.poutreBasse?.chemin || [],
                        rayon: 0.05
                      },
                      portes: (portes && portes.parametres.actif) ? (
                        portes.elements.map(el => {
                          const coins = el.geometrie.coins || [];
                          const ring = Math.floor(coins.length / 2);
                          const bas = coins.slice(0, ring);
                          const haut = coins.slice(ring);
                          const yMin = bas.length > 0 ? Math.min(...bas.map(p => p.y)) : 0;
                          const yMax = haut.length > 0 ? Math.max(...haut.map(p => p.y)) : yMin;
                          // baseCurve = anneau du bas (déjà décalé légèrement pour éviter z-fighting)
                          return { baseCurve: bas, yMin, yMax };
                        })
                      ) : undefined
                    }}
                    onTraversesChange={handleTraversesChange}
                    onError={handleModuleError}
                  />
                ) : (
                  <div style={{ 
                    opacity: 0.5, 
                    position: 'relative',
                    pointerEvents: 'none',
                    minHeight: '100px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#2d2d2d',
                    border: '1px solid #404040',
                    borderRadius: '8px',
                    margin: '8px 0'
                  }}>
                    <div style={{
                      backgroundColor: 'rgba(255, 0, 0, 0.8)',
                      color: 'white',
                      padding: '10px 20px',
                      borderRadius: '5px',
                      fontWeight: 'bold'
                    }}>                TRAVERSES DÉSACTIVÉES              </div>
                  </div>
                )
              )}

              {/* Module 3: Les Soudures */}
              {structure && (
                isModuleActive('soudures') ? (
                  <SouduresModule
                    donneesTonnelle={{
                      structureBase: {
                        montants: structure.montants.map(m => ({
                          id: m.id,
                          position: { x: m.position.x, y: m.position.y, z: m.position.z },
                          hauteur: m.hauteur
                        })),
                        poutres: {
                          poutreHaute: {
                            points: structure.poutres?.poutreHaute?.chemin.map(p => ({ x: p.x, y: p.y, z: p.z })) || []
                          },
                          poutreBasse: {
                            points: structure.poutres?.poutreBasse?.chemin.map(p => ({ x: p.x, y: p.y, z: p.z })) || []
                          }
                        }
                      },
                      traverses: traverses ? {
                        rangees: traverses.rangees.map(rangee => ({
                          id: rangee.id,
                          hauteur: rangee.hauteur,
                          traverses: rangee.traverses.map(t => ({
                            id: t.id,
                            points: t.points
                          }))
                        }))
                      } : undefined,
                      doublePoutre: doublePoutre ? {
                        montants: doublePoutre.montants.map(m => ({
                          id: m.id,
                          position: { x: m.position.x, y: m.position.y, z: m.position.z },
                          hauteur: m.hauteur
                        })),
                        poutres: [
                          {
                            id: doublePoutre.poutres.poutreBasse.id,
                            debut: doublePoutre.poutres.poutreBasse.chemin[0],
                            fin: doublePoutre.poutres.poutreBasse.chemin[doublePoutre.poutres.poutreBasse.chemin.length - 1]
                          },
                          {
                            id: doublePoutre.poutres.poutreHaute.id,
                            debut: doublePoutre.poutres.poutreHaute.chemin[0],
                            fin: doublePoutre.poutres.poutreHaute.chemin[doublePoutre.poutres.poutreHaute.chemin.length - 1]
                          }
                        ]

                      } : undefined
                    }}
                    onSouduresChange={handleSouduresChange}
                    onError={handleModuleError}
                  />
                ) : (
                  <div style={{ 
                    opacity: 0.5, 
                    position: 'relative',
                    pointerEvents: 'none',
                    minHeight: '100px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#2d2d2d',
                    border: '1px solid #404040',
                    borderRadius: '8px',
                    margin: '8px 0'
                  }}>
                    <div style={{
                      backgroundColor: 'rgba(255, 0, 0, 0.8)',
                      color: 'white',
                      padding: '10px 20px',
                      borderRadius: '5px',
                      fontWeight: 'bold'
                    }}>                SOUDURES DÉSACTIVÉES              </div>
                  </div>
                )
              )}

              {/* Module 4: Double Poutre */}
              {structure && (
                isModuleActive('double-poutre') ? (
                  <DoublePoutreModule
                    actif={doublePoutreActive}
                    onToggle={handleDoublePoutreToggle}
                    structurePrincipale={{
                      type: structure.parametres.type,
                      nombreCotes: structure.parametres.nombreCotes,
                      longueur: structure.parametres.longueur || structure.parametres.largeur,
                      largeur: structure.parametres.largeur,
                      hauteur: structure.parametres.hauteur, // Hauteur de la structure principale
                      materiau: structure.parametres.materiau,
                      couleur: structure.parametres.couleur,
                      epaisseur: structure.parametres.epaisseur,
                      montants: structure.montants || [],
                      poutres: {
                        poutreHaute: {
                          chemin: structure.poutres?.poutreHaute?.chemin || []
                        }
                      }
                    }}
                    callbacks={{
                      onStructureChange: handleDoublePoutreChange,
                      onTraversesDoublePoutreChange: handleTraversesDoublePoutreChange,
                      onSauvegarde: (config: any) => {
                      }
                    }}
                  />
                ) : (
                  <div style={{ opacity: 0.5, position: 'relative', pointerEvents: 'none', minHeight: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#2d2d2d', border: '1px solid #404040', borderRadius: '8px', margin: '8px 0' }}>
                    <div style={{ backgroundColor: 'rgba(255, 0, 0, 0.8)', color: 'white', padding: '6px 12px', borderRadius: '5px', fontWeight: 'bold' }}>PORTES DÉSACTIVÉES</div>
                  </div>
                )
              )}

              {/* Module 5: Le Toit */}
              {structure && (
                isModuleActive('toit') ? (
                  <ToitModule
                    donneesStructureBase={{
                      forme: structure.parametres.type as string,
                      largeur: structure.parametres.largeur,
                      longueur: structure.parametres.type === 'cercle' || structure.parametres.type === 'carre'
                        ? structure.parametres.largeur
                        : (structure.parametres.longueur || structure.parametres.largeur),
                      hauteur: structure.parametres.hauteur,
                      nombreMontants: structure.montants.length,
                      positionsMontants: structure.montants.map(m => ({ x: m.position.x, y: m.position.y, z: m.position.z })),
                      poutreHaute: {
                        points: structure.poutres?.poutreHaute?.chemin || [],
                        hauteur: structure.parametres.hauteur // Utiliser la vraie hauteur de la structure
                      },
                      doublePoutreActive: doublePoutreActive && !!doublePoutre,
                      doublePoutre: doublePoutre ? {
                        points: doublePoutre.poutres?.poutreHaute?.chemin || [],
                        hauteur: (structure.parametres.hauteur || 2.5) + (doublePoutre.parametres?.hauteur || 0.5)
                      } : undefined,
                      centre: calculerCentreGeometrique(structure),
                      materiau: structure.parametres.materiau,
                      epaisseur: structure.parametres.epaisseur,
                      couleur: structure.parametres.couleur
                    }}
                    onStructureChange={handleToitChange}
                    onParametresChange={handleToitParametresChange}
                  />
                ) : (
                  <div style={{ 
                    opacity: 0.5, 
                    position: 'relative',
                    pointerEvents: 'none',
                    minHeight: '100px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#2d2d2d',
                    border: '1px solid #404040',
                    borderRadius: '8px',
                    margin: '8px 0'
                  }}>
                    <div style={{
                      backgroundColor: 'rgba(255, 0, 0, 0.8)',
                      color: 'white',
                      padding: '10px 20px',
                      borderRadius: '5px',
                      fontWeight: 'bold'
                    }}>                TOIT DÉSACTIVÉ              </div>
                  </div>
                )
              )}

              {/* Module 6: Accessoires et Environnement */}
              {structure && (
                isModuleActive('accessoires') ? (
                  <AccessoiresModule
                    onStructureChange={setAccessoires}
                    hauteurStructure={structure.parametres.hauteur || 2.2}
                  />
                ) : (
                  <div style={{ 
                    opacity: 0.5, 
                    position: 'relative',
                    pointerEvents: 'none',
                    minHeight: '100px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#2d2d2d',
                    border: '1px solid #404040',
                    borderRadius: '8px',
                    margin: '8px 0'
                  }}>
                    <div style={{
                      backgroundColor: 'rgba(255, 0, 0, 0.8)',
                      color: 'white',
                      padding: '10px 20px',
                      borderRadius: '5px',
                      fontWeight: 'bold'
                    }}>                ACCESSOIRES DÉSACTIVÉS              </div>
                  </div>
                )
              )}

              {/* Module 7: Les Parois */}
              {structure && (
                isModuleActive('parois') ? (
                  <ParoisModule
                    donneesStructureBase={{
                      forme: structure.parametres.type as string,
                      largeur: structure.parametres.largeur,
                      longueur: structure.parametres.type === 'cercle' || structure.parametres.type === 'carre'
                        ? structure.parametres.largeur
                        : (structure.parametres.longueur || structure.parametres.largeur),
                      hauteur: structure.parametres.hauteur,
                      nombreMontants: structure.montants.length,
                      positionsMontants: structure.montants.map(m => ({ x: m.position.x, y: m.position.y, z: m.position.z })),
                      materiau: structure.parametres.materiau,
                      epaisseur: structure.parametres.epaisseur,
                      couleur: structure.parametres.couleur,
                      poutreHaute: { points: structure.poutres?.poutreHaute?.chemin || [] },
                      poutreBasse: { points: structure.poutres?.poutreBasse?.chemin || [] },
                      centre: calculerCentreGeometrique(structure)
                    } as DonneesStructureBasePourParois}
                    onStructureChange={handleParoisChange}
                    onError={(e) => setError(e)}
                  />
                ) : (
                  <div style={{ opacity: 0.5, position: 'relative', pointerEvents: 'none', minHeight: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#2d2d2d', border: '1px solid #404040', borderRadius: '8px', margin: '8px 0' }}>
                    <div style={{ backgroundColor: 'rgba(255, 0, 0, 0.8)', color: 'white', padding: '6px 12px', borderRadius: '5px', fontWeight: 'bold' }}>PAROIS DÉSACTIVÉES</div>
                  </div>
                )
              )}

              {/* Module 8: Les Portes */}
              {structure && (
                isModuleActive('portes') ? (
                  <PortesModule
                    donneesStructureBase={{
                      forme: structure.parametres.type as string,
                      largeur: structure.parametres.largeur,
                      longueur: structure.parametres.type === 'cercle' || structure.parametres.type === 'carre'
                        ? structure.parametres.largeur
                        : (structure.parametres.longueur || structure.parametres.largeur),
                      hauteur: structure.parametres.hauteur,
                      nombreMontants: structure.montants.length,
                      positionsMontants: structure.montants.map(m => ({ x: m.position.x, y: m.position.y, z: m.position.z })),
                      materiau: structure.parametres.materiau,
                      epaisseur: structure.parametres.epaisseur,
                      couleur: structure.parametres.couleur,
                      poutreHaute: { points: structure.poutres?.poutreHaute?.chemin || [] },
                      poutreBasse: { points: structure.poutres?.poutreBasse?.chemin || [] },
                      centre: calculerCentreGeometrique(structure)
                    } as DonneesStructureBasePourPortes}
                    onStructureChange={handlePortesChange}
                  />
                ) : (
                  <div style={{ opacity: 0.5, position: 'relative', pointerEvents: 'none', minHeight: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#2d2d2d', border: '1px solid #404040', borderRadius: '8px', margin: '8px 0' }}>
                    <div style={{ backgroundColor: 'rgba(255, 0, 0, 0.8)', color: 'white', padding: '6px 12px', borderRadius: '5px', fontWeight: 'bold' }}>PORTES DÉSACTIVÉES</div>
                  </div>
                )
              )}
              {/* Liste des modules à venir */}
              <div style={styles.modulesList}>
                <div style={{ ...styles.moduleItem, ...styles.moduleActive }}>
                  ✅ Module 1: Structure de Base
                </div>
                <div style={{ ...styles.moduleItem, ...styles.moduleActive }}>
                  ✅ Module 2: Les Traverses
                </div>
                <div style={{ ...styles.moduleItem, ...styles.moduleActive }}>
                  ✅ Module 3: Les Soudures
                </div>
                <div style={{ ...styles.moduleItem, ...styles.moduleActive }}>
                  ✅ Module 4: Double Poutre
                </div>
                <div style={{ ...styles.moduleItem, ...styles.moduleActive }}>
                  ✅ Module 5: Le Toit
                </div>
                <div style={styles.moduleItem}>
                  ⏳ Module 6: Accessoires
                </div>
                <div style={styles.moduleItem}>
                  ⏳ Module 7: Les Parois
                </div>
                <div style={styles.moduleItem}>
                  ⏳ Module 8: Les Portes
                </div>
                <div style={styles.moduleItem}>
                  ⏳ Module 9: Frise Décorative
                </div>
                <div style={styles.moduleItem}>
                  ⏳ Module 10: Visualisation Avancée
                </div>
                <div style={styles.moduleItem}>
                  ⏳ Module 11: Gestion Projets
                </div>
                <div style={styles.moduleItem}>
                  ⏳ Module 12: Export PDF
                </div>
                <div style={styles.moduleItem}>
                  ⏳ Module 13: E-commerce
                </div>
                <div style={styles.moduleItem}>
                  ⏳ Module 14: Galerie
                </div>
              </div>
              </div>
      
            {/* Zone principale de visualisation */}
            <div style={styles.main} className="app-main">
              <div style={styles.content}>
                {/* Statut de l'application */}
                
                {error && (
                  <div style={styles.error}>
                    ❌ {error}
                  </div>
                )}
                
                {/* Visualisateur 3D */}
                <Visualisateur3D
                  structure={structure}
                  traverses={traverses || undefined}
                  soudures={soudures || undefined}
                  doublePoutre={doublePoutre || undefined}
                  traversesDoublePoutre={traversesDoublePoutre || undefined}
                  toit={toit || undefined}
                  accessoires={accessoires || null}
                  parois={parois || null}
                  portes={portes || null}
                  showGrid={true}
                  showStats={false}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </AuthManager>
  );
}

export default App;
